/** SC-1280 — les 14 allergènes majeurs (règlement UE 1169/2011). Mêmes codes que
 *  le catalogue : `allergens` de `GET resource/catalog/product/`. */
export const ALLERGEN_CODES = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soy',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const;

export type AllergenCode = (typeof ALLERGEN_CODES)[number];

/** Ce que le restaurant a déclaré d'un produit : ses allergènes (liste vide =
 *  « sans allergène majeur ») et s'il « peut contenir des traces » (les codes de traces
 *  ne sont jamais affichés, seulement ce drapeau). */
export interface ProductAllergens {
  contains: AllergenCode[];
  traces: boolean;
}

const KNOWN = new Set<string>(ALLERGEN_CODES);

/** Champ `allergens` d'un produit du catalogue (`{contains, may_contain}`) → sa
 *  déclaration, ou `undefined` = non renseigné. Jamais d'exception : tout ce qui
 *  n'est pas lisible est ignoré. Les codes inconnus sont écartés, l'ordre est
 *  conservé. */
export function parseProductAllergens(raw: unknown): ProductAllergens | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const { contains, may_contain } = raw as { contains?: unknown; may_contain?: unknown };
  if (!Array.isArray(contains)) return undefined;
  const valid = contains.filter(
    (c, i): c is AllergenCode => typeof c === 'string' && KNOWN.has(c) && contains.indexOf(c) === i,
  );
  // Seule une liste source explicitement vide dit « sans allergène majeur ». Une
  // liste dont tout a été rejeté est illisible, pas vide : produit non renseigné.
  if (valid.length === 0 && contains.length > 0) return undefined;
  const traces = Array.isArray(may_contain) && may_contain.some((c) => typeof c === 'string' && KNOWN.has(c));
  return { contains: valid, traces };
}
